import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

import { MENU_IGNORED_ROUTES, QueryKeys } from "@/constants";

import { House } from "lucide-react";
import { MenusSkeleton } from "@/pages/home/menus-skeleton";
import { useFeatures } from "@/hooks/use-features";
import { useScrollFade } from "@/hooks/use-scroll-fade";
import useUserInfo from "@/hooks/use-user-info";
import type { Feature } from "@/model/features";
import { AuthMode } from "@/context/auth-provider";

import useGuestCheckSinglePage from "@/hooks/use-guest-check-singlepage";
import { queryClient } from "@/hooks/use-query-client";
import ScreenRouteNames from "@/constants/screen-route-names";
import { Badge } from "@/components/ui/badge";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCsrUnreadCount,
  setUnreadCount,
} from "@/store/csr-chat/csr-chat-slice";
import { useChatChannels } from "@/hooks/use-csr-chat";
import { lowerCaseComparison } from "@/lib/string-util";
import {
  selectNotificationUnreadCount,
  setNotificationUnreadCount,
} from "@/store/notification/notification-slice";
import { useNotification } from "@/hooks/use-notification";

const LeftSidebar: React.FC = () => {
  const userInfo = useUserInfo();

  const menuItemsQuery = useFeatures();
  const isMember = userInfo.mode === AuthMode.MEMBER;
  const showCsrChat =
    isMember &&
    Boolean(
      (
        queryClient.getQueryData([
          QueryKeys.GLOBAL_FLAGS,
          userInfo.username,
        ]) as { data?: { showChat?: boolean }[] } | undefined
      )?.data?.[0]?.showChat,
    );
  const dispatch = useDispatch();
  const { restrictPage } = useGuestCheckSinglePage();
  const unreadMessageCount = useSelector(selectCsrUnreadCount);
  const notificationUnreadCount = useSelector(selectNotificationUnreadCount);
  const { data: channels } = useChatChannels(showCsrChat);
  const { data: notificationsData } = useNotification(userInfo);
  const unreadInitialized = useRef(false);
  const notificationUnreadInitialized = useRef(false);
  // The menu is the only thing on this panel that can outgrow it, so the
  // bar belongs to the scrolling list rather than the panel around it.
  const menuScrollRef = useScrollFade<HTMLDivElement>();

  useEffect(() => {
    if (!channels || !showCsrChat || unreadInitialized.current) return;
    const total = channels.reduce(
      (sum, ch) => sum + (ch.totalUnreadMessage ?? 0),
      0,
    );
    dispatch(setUnreadCount(total));
    unreadInitialized.current = true;
  }, [channels, showCsrChat, dispatch]);

  useEffect(() => {
    if (!notificationsData || notificationUnreadInitialized.current) return;
    const unread = notificationsData.filter((n) => !n.isRead).length;
    dispatch(setNotificationUnreadCount(unread));
    notificationUnreadInitialized.current = true;
  }, [notificationsData, dispatch]);

  if (menuItemsQuery.isLoading) {
    return <MenusSkeleton />;
  }

  const menuItems: Feature[] = menuItemsQuery.data ?? [];

  const handleClick = (e: React.MouseEvent) => {
    if (e.button === 1 || e.metaKey || e.ctrlKey) {
      e.preventDefault();
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  if (restrictPage) {
    return (
      <Sidebar className="hide-from-third-app bottom-8 top-20 h-auto">
        <SidebarContent className="bg-white" />
      </Sidebar>
    );
  } else {
    return (
      <Sidebar className="hide-from-third-app bottom-8 top-20 h-auto">
        {/* Scrolls once the menu outgrows the panel; the bar shows only
            while it is being scrolled — see `use-scroll-fade`. */}
        <SidebarContent ref={menuScrollRef} className="scrollbar-fade bg-white">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <div className="mb-2 cursor-pointer px-2 py-[22px] font-semibold">
                      <NavLink
                        to="/home"
                        onClick={handleClick}
                        onContextMenu={handleContextMenu}
                        className={({ isActive }) =>
                          `w-full rounded-sm border-l-4 text-black no-underline ${
                            isActive
                              ? "flex h-11 items-center border-primary bg-primary-light hover:bg-primary-extraLight"
                              : "h-11 border-transparent hover:bg-primary-extraLight"
                          }`
                        }
                      >
                        <div className="flex size-full items-center justify-start gap-4 pl-3 text-primary">
                          <House className="" strokeWidth={1.5} size={28} />
                          <span className="text-medium font-semibold text-black">
                            Dashboard
                          </span>
                        </div>
                      </NavLink>
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                {menuItems?.map((menuItem, index) => {
                  const igonredFeatures = MENU_IGNORED_ROUTES.find((route) => {
                    return (
                      route.path === menuItem.action?.web_route ||
                      menuItem.phone ||
                      menuItem.web_link
                    );
                  });

                  if (igonredFeatures) return null;

                  const isNotificationFeatureEnabled = lowerCaseComparison(
                    menuItem?.action?.web_route,
                    ScreenRouteNames.NOTIFICATION.path,
                  );
                  const isCsrChatFeatureEnabled = lowerCaseComparison(
                    menuItem?.action?.web_route,
                    ScreenRouteNames.CSR_CHAT.path,
                  );

                  return (
                    <SidebarMenuItem key={index}>
                      <SidebarMenuButton asChild disabled={!menuItem.active}>
                        <div className="mb-2 cursor-pointer px-2 py-[22px] font-semibold">
                          <NavLink
                            to={`/${menuItem.action?.web_route}`}
                            onClick={handleClick}
                            onContextMenu={handleContextMenu}
                            className={({ isActive }) =>
                              `w-full rounded-sm border-l-4 text-black no-underline ${
                                isActive
                                  ? "flex h-11 items-center border-primary bg-primary-light hover:bg-primary-extraLight"
                                  : "h-11 border-transparent hover:bg-primary-extraLight"
                              }`
                            }
                          >
                            <div className="flex size-full items-center justify-start gap-4 pl-3">
                              <img
                                src={`data:image/svg+xml;base64,${menuItem.icon}`}
                                alt={menuItem.name}
                                className="aspect-square h-7 w-fit"
                              />
                              <div className="flex gap-2">
                                <span className="text-medium font-semibold text-black">
                                  {menuItem.name}
                                </span>
                                {unreadMessageCount > 0 &&
                                  showCsrChat &&
                                  isCsrChatFeatureEnabled && (
                                    <Badge className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-yellow-500 px-1.5 text-xs font-bold leading-none text-black shadow-sm">
                                      {unreadMessageCount > 9
                                        ? "9+"
                                        : unreadMessageCount}
                                    </Badge>
                                  )}
                                {notificationUnreadCount > 0 &&
                                  isNotificationFeatureEnabled && (
                                    <Badge className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-yellow-500 px-1.5 text-xs font-bold leading-none text-black shadow-sm">
                                      {notificationUnreadCount > 9
                                        ? "9+"
                                        : notificationUnreadCount}
                                    </Badge>
                                  )}
                              </div>
                            </div>
                          </NavLink>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    );
  }
};

export default LeftSidebar;
